import type { ReactNode } from 'react';

import type {
  FundTransactionDetail,
  ReconciliationStateResponse,
} from '@/types/fund-transactions';

import { AssetSaleDetailsCard } from './AssetSaleDetailsCard';
import { ConvertibleDetailsCard } from './ConvertibleDetailsCard';
import { CurrencyConversionDetailsCard } from './CurrencyConversionDetailsCard';
import { DistributionDetailsCard } from './DistributionDetailsCard';
import { FinancingRoundDetailsCard } from './FinancingRoundDetailsCard';
import { FxPeriodEndDetailsCard } from './FxPeriodEndDetailsCard';
import {
  DealCostDetailsCard,
  DrawdownDetailsCard,
} from './GenericCostDetailsCard';
import { FundCostDetailsCard } from './FundCostDetailsCard';
import { FundCostRecognitionDetailsCard } from './FundCostRecognitionDetailsCard';
import { LpTransferDetailsCard } from './LpTransferDetailsCard';
import { ManagementFeeDetailsCard } from './ManagementFeeDetailsCard';
import { PurchaseDetailsCard } from './PurchaseDetailsCard';

export type DetailsCardProps = {
  transaction: FundTransactionDetail;
  fundId: string;
  fundName: string;
  fundCurrency: string;
  reconciliation: ReconciliationStateResponse | undefined;
  onViewJournalEntries: (() => void) | undefined;
  /** Discards a pending FX period end. Only that card offers it. */
  onDiscardPeriodEnd: () => Promise<void>;
  /** Deletes a draft currency conversion. Only that card offers it. */
  onDeleteConversion: () => Promise<void>;
  /** Deletes a draft LP transfer. Only that card offers it. */
  onDeleteLpTransfer: () => Promise<void>;
  /** Shown beside an investment's description, e.g. its LP allocation. */
  investmentDescriptionAside: ReactNode;
};

export function renderDetailsCard({
  transaction,
  fundId,
  fundName,
  fundCurrency,
  reconciliation,
  onViewJournalEntries,
  onDiscardPeriodEnd,
  onDeleteConversion,
  onDeleteLpTransfer,
  investmentDescriptionAside,
}: DetailsCardProps) {
  switch (transaction.transaction_type) {
    case 'DISTRIBUTION':
      return (
        <DistributionDetailsCard
          fundId={fundId}
          transaction={transaction}
          fundName={fundName}
          reconciliation={reconciliation}
          onViewJournalEntries={onViewJournalEntries}
        />
      );
    case 'MANAGEMENT_FEE':
    case 'MANAGEMENT_FEE_RECOGNITION':
      return (
        <ManagementFeeDetailsCard
          transaction={transaction}
          fundId={fundId}
          fundName={fundName}
          reconciliation={reconciliation}
          onViewJournalEntries={onViewJournalEntries}
        />
      );
    case 'FUND_COST':
      return (
        <FundCostDetailsCard
          transaction={transaction}
          fundId={fundId}
          fundName={fundName}
          reconciliation={reconciliation}
          onViewJournalEntries={onViewJournalEntries}
        />
      );
    case 'FUND_COST_RECOGNITION':
      return (
        <FundCostRecognitionDetailsCard
          transaction={transaction}
          fundName={fundName}
          reconciliation={reconciliation}
          onViewJournalEntries={onViewJournalEntries}
        />
      );
    case 'DEAL_COST':
      return (
        <DealCostDetailsCard
          transaction={transaction}
          fundName={fundName}
          reconciliation={reconciliation}
          onViewJournalEntries={onViewJournalEntries}
        />
      );
    case 'DRAWDOWN':
      return (
        <DrawdownDetailsCard
          transaction={transaction}
          fundName={fundName}
          reconciliation={reconciliation}
          onViewJournalEntries={onViewJournalEntries}
        />
      );
    case 'ASSET_SALE':
      return (
        <AssetSaleDetailsCard
          transaction={transaction}
          fundName={fundName}
          reconciliation={reconciliation}
          onViewJournalEntries={onViewJournalEntries}
        />
      );
    case 'INVESTMENT_PURCHASE':
      return (
        <PurchaseDetailsCard
          transaction={transaction}
          fundName={fundName}
          reconciliation={reconciliation}
          onViewJournalEntries={onViewJournalEntries}
          descriptionAside={investmentDescriptionAside}
        />
      );
    case 'FINANCING_ROUND':
      return (
        <FinancingRoundDetailsCard
          transaction={transaction}
          fundName={fundName}
          reconciliation={reconciliation}
          onViewJournalEntries={onViewJournalEntries}
          descriptionAside={investmentDescriptionAside}
        />
      );
    case 'CONVERTIBLE_INVESTMENT':
    case 'CONVERTIBLE_CONVERSION':
    case 'CONVERTIBLE_REPAYMENT':
      return (
        <ConvertibleDetailsCard
          transaction={transaction}
          fundName={fundName}
          reconciliation={reconciliation}
          onViewJournalEntries={onViewJournalEntries}
          descriptionAside={investmentDescriptionAside}
        />
      );
    case 'CURRENCY_CONVERSION':
      return (
        <CurrencyConversionDetailsCard
          transaction={transaction}
          fundName={fundName}
          fundCurrency={fundCurrency}
          reconciliation={reconciliation}
          onViewJournalEntries={onViewJournalEntries}
          onDelete={onDeleteConversion}
        />
      );
    case 'FX_PERIOD_END':
      return (
        <FxPeriodEndDetailsCard
          transaction={transaction}
          fundId={fundId}
          fundName={fundName}
          reconciliation={reconciliation}
          onViewJournalEntries={onViewJournalEntries}
          onDiscard={onDiscardPeriodEnd}
        />
      );
    case 'LP_TRANSFER':
      return (
        <LpTransferDetailsCard
          transaction={transaction}
          fundId={fundId}
          fundName={fundName}
          reconciliation={reconciliation}
          onViewJournalEntries={onViewJournalEntries}
          onDelete={onDeleteLpTransfer}
        />
      );
    default: {
      const _exhaustive: never = transaction.transaction_type;
      return _exhaustive;
    }
  }
}
